/**
 * Angel — „Buffy the Vampire Slayer" (Angel a Faith).
 *
 * Schopnosť: keď Angel alebo Faith útočí a súboj prehrá, hráč si potiahne
 * 1 kartu. Veľa kariet má efekt „ak si prehral súboj" — Angel trestá súpera
 * aj pri neúspechu. Prehra útočníka = nespôsobil combat damage; prehra
 * obrancu = combat damage dostal.
 */

import { neighbors } from '../map.js';
import type { Ctx } from '../ops.js';
import { askChoice, dealDamage, drawCard, getMap, healFighter } from '../ops.js';
import { areEnemies } from '../players.js';
import type { PlayerIndex } from '../types.js';
import { disengage, draw, feint, momentousShift, regroup } from './common.js';
import { askMove, emptySpacesInZone, fighterOrNull } from './helpers.js';
import type { CardEffect, EffectRegistry, HeroSpecial } from './types.js';

const ANGEL = 'angel';
const FAITH = 'faith';

const faithOf = (ctx: Ctx, player: PlayerIndex) =>
  ctx.state.fighters.find((f) => f.owner === player && f.defId === FAITH) ?? null;

/** Súperove figúrky susediace s Faith. */
function enemiesNextToFaith(ctx: Ctx, player: PlayerIndex): string[] {
  const faith = faithOf(ctx, player);
  if (!faith) return [];
  const around = new Set(neighbors(getMap(ctx), faith.spaceId));
  return ctx.state.fighters
    .filter((f) => areEnemies(ctx.state, f.owner, player) && around.has(f.spaceId))
    .map((f) => f.id);
}

/** „Move Faith up to 5 spaces. She may move through opposing fighters." */
const fiveByFive: CardEffect = {
  after: (e) => {
    const faith = faithOf(e.ctx, e.player);
    if (!faith) return;
    askMove(e, { fighters: [faith.id], max: 5, through: true, label: 'Five by Five: presuň Faith až o 5 (smie prejsť cez súperov)' });
  },
};

/** „Deal 2 damage to one opposing fighter adjacent to Faith." */
const killerOfTheDead: CardEffect = {
  scheme: (e) => {
    askChoice(e.ctx, {
      kind: 'damage',
      player: e.player,
      source: e.cardId,
      label: 'Killer of the Dead: 2 poškodenia súperovej figúrke pri Faith',
      optional: false,
      amount: 2,
      targets: enemiesNextToFaith(e.ctx, e.player),
    });
  },
};

/** „If you lost the combat, recover 1 health." (Angel) */
const cursedWithASoul: CardEffect = {
  after: (e) => {
    if (e.won) return;
    if (e.fighterId) healFighter(e.ctx, e.fighterId, 1);
  },
};

/** „If you lost the combat, you may place Angel in any space in his zone." */
const hauntedByTheFaces: CardEffect = {
  after: (e) => {
    if (e.won) return;
    const angel = fighterOrNull(e.ctx, e.fighterId);
    if (angel?.defId !== ANGEL) return;
    askChoice(e.ctx, {
      kind: 'place',
      player: e.player,
      source: e.cardId,
      label: 'Haunted by the Faces: smieš položiť Angela na priestor v jeho zóne',
      optional: true,
      fighterId: angel.id,
      spaces: emptySpacesInZone(e.ctx, angel.id),
    });
  },
};

/** „If you lost the combat, the opposing fighter takes 1 damage." */
const brooding: CardEffect = {
  after: (e) => {
    if (!e.won && e.opponentFighterId) dealDamage(e.ctx, e.opponentFighterId, 1);
  },
};

/** „Deal 1 damage to each opposing fighter adjacent to Faith." */
const theRogueSlayer: CardEffect = {
  after: (e) => {
    for (const id of enemiesNextToFaith(e.ctx, e.player)) dealDamage(e.ctx, id, 1);
  },
};

export const angelCards: EffectRegistry = {
  'angel.feint': feint,
  'angel.wisdom-of-ages': draw(1),
  'angel.five-by-five': fiveByFive,
  'angel.killer-of-the-dead': killerOfTheDead,
  'angel.cursed-with-a-soul': cursedWithASoul,
  'angel.haunted-by-the-faces': hauntedByTheFaces,
  'angel.brooding': brooding,
  'angel.regroup': regroup,
  'angel.momentous-shift': momentousShift,
  'angel.disengage': disengage,
  'angel.the-rogue-slayer': theRogueSlayer,
  // Angelus, Scourge of Europe: bez efektu, len hodnota 5.
};

export const angelSpecial: HeroSpecial = {
  name: 'Angel a Faith',
  /** „After Angel or Faith attacks, if you lost the combat, draw 1 card." — len ako útočník. */
  afterCombat: (ctx, player, _fighterId, won) => {
    const combat = ctx.state.combat;
    if (won || combat?.attackerPlayer !== player) return;
    // Útočník mohol medzitým padnúť efektom — potom ho hľadáme medzi porazenými.
    const attacker =
      fighterOrNull(ctx, combat.attackerFighter) ?? ctx.state.defeated?.find((f) => f.id === combat.attackerFighter);
    if (attacker?.defId !== ANGEL && attacker?.defId !== FAITH) return;
    drawCard(ctx, player);
  },
};
